import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Command } from 'cmdk';
import { useProjectId } from '~/hooks/context/useProjectId';
import { api } from '~/trpc-extra/api';
import { ScrollArea, useMantineColorScheme, Modal, useComputedColorScheme } from '@mantine/core';
import {
  IconPlus,
  IconMoonStars,
  IconPlayerPlay,
  IconArrowDown,
  IconArrowUp,
  IconSun,
  IconBuildingCommunity,
  IconArrowBack,
} from '@tabler/icons-react';
import { useRouter } from 'next/router';
import { useCreateAndNavToTest } from '~/hooks/api/useCreateAndNavToTest';
import { useRedirectFunctions } from '@propelauth/react';
import { useCommandMenu } from '~/hooks/search/useCommandMenu';

import { StyledCommandItem } from './command/CommandItem';
import { CommandGroupTitle } from './command/CommandGroupTitle';
import { DetailsPanel } from './details/DetailsPanel';
import { SearchButton } from './button/SearchButton';
import { FilterDrawer } from './input/FilterDrawer';
import { SearchInput } from './input/SearchInput';
import { SearchControls } from './input/SearchControls';
import { CommandEmpty } from './command/CommandEmpty';
import { CommandTestSuiteGroup } from './command/CommandTestSuiteGroup';
import { SuiteDetailsPanel } from './details/SuiteDetailsPanel';

const AVAILABLE_FILTERS = ['Tests', 'Test Drafts', 'Test Suites', 'Commands'] as const;

export default function UniversalSearchButton({ hidden }: { hidden?: boolean }) {
  const router = useRouter();
  const { toggleColorScheme } = useMantineColorScheme();
  const colorScheme = useComputedColorScheme();
  const projectId = useProjectId();
  const { commandMenuOpened, openCommandMenu, closeCommandMenu } = useCommandMenu();
  const [search, setSearch] = useState('');
  const [value, setValue] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [activeFilters, setActiveFilters] = useState<string[]>([]);

  const searchInputRef = useRef<HTMLInputElement>(null);

  const createAndNavToTest = useCreateAndNavToTest({});
  const { redirectToOrgPage } = useRedirectFunctions();

  const { data: tests = [] } = api.test.listTests.useQuery(
    { projectId: projectId || '' },
    { enabled: commandMenuOpened && !!projectId },
  );
  const { data: testDrafts = [] } = api.testDraft.list.useQuery(
    { projectId: projectId || '' },
    { enabled: commandMenuOpened && !!projectId },
  );
  const { data: testSuites = [] } = api.testSuite.listWithLatestRun.useQuery(
    { projectId: projectId || '' },
    { enabled: commandMenuOpened && !!projectId },
  );

  const userIds = useMemo(
    () => [
      ...new Set(
        [
          ...(testDrafts?.map((draft) => draft?.createdByUserId) || []),
          ...(tests?.map((test) => test?.createdByUserId) || []),
        ].filter((userId) => userId !== null),
      ),
    ],
    [testDrafts, tests],
  );
  const { data: users } = api.team.getUsersInfo.useQuery({ userIds }, { enabled: userIds.length > 0 });

  const hoveredTest = useMemo(() => {
    return tests?.find((test) => test.id === value);
  }, [value, tests]);

  const hoveredTestDraft = useMemo(() => {
    return testDrafts?.find((testDraft) => testDraft.id === value);
  }, [value, testDrafts]);

  const hoveredTestSuite = useMemo(() => {
    return testSuites?.find((testSuite) => testSuite.id === value);
  }, [value, testSuites]);

  // clear search when command menu is opened
  useEffect(() => {
    setSearch('');
  }, [commandMenuOpened]);

  // focus on searchbar when unfocused and an input is entered
  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      if (commandMenuOpened && document.activeElement !== searchInputRef.current) {
        if (e.metaKey || e.ctrlKey || e.altKey) {
          return;
        }

        searchInputRef.current?.focus();
      }
    };

    document.addEventListener('keypress', handleKeyPress);
    return () => document.removeEventListener('keypress', handleKeyPress);
  }, [commandMenuOpened]);

  const toggleFilter = (filter: string) => {
    setActiveFilters((prev) => (prev.includes(filter) ? prev.filter((f) => f !== filter) : [...prev, filter]));
  };

  return (
    <>
      <SearchButton
        onClick={openCommandMenu}
        hidden={hidden}
      />

      <Modal
        opened={commandMenuOpened}
        onClose={closeCommandMenu}
        size="xl"
        centered
        trapFocus={true}
        withCloseButton={false}
        classNames={{
          body: 'p-0',
          content: 'bg-transparent',
        }}
      >
        <Command
          value={value}
          onValueChange={setValue}
          className="flex w-full flex-col gap-4 focus:outline-none"
        >
          <div className="flex flex-col rounded-xl border border-[#dee2e6] bg-transparent bg-white text-gray-700 placeholder:text-gray-400 dark:border-[#424242] dark:bg-[#242424] dark:text-gray-200 dark:placeholder:text-gray-500">
            <SearchInput
              searchRef={searchInputRef}
              value={search}
              onChange={setSearch}
            />
            <SearchControls
              showFilters={showFilters}
              activeFilters={activeFilters}
              setShowFilters={setShowFilters}
              closeModal={closeCommandMenu}
            />
            <FilterDrawer
              showFilters={showFilters}
              activeFilters={activeFilters}
              availableFilters={AVAILABLE_FILTERS}
              onToggleFilter={toggleFilter}
              onClearFilters={() => setActiveFilters([])}
            />
          </div>

          <Command.List className="rounded-xl border border-[#dee2e6] bg-transparent bg-white py-2 text-gray-700 shadow-lg outline-none placeholder:text-gray-400 dark:border-[#424242] dark:bg-[#242424] dark:text-gray-200 dark:placeholder:text-gray-500">
            <div className="flex">
              <ScrollArea.Autosize
                className={`h-[30rem] pt-1 ${hoveredTest || hoveredTestDraft || hoveredTestSuite ? 'w-2/3' : 'w-full'}`}
                scrollbarSize={6}
              >
                <div className="px-4">
                  {/* Empty state */}
                  <CommandEmpty />

                  {/* Quick actions */}
                  {(!activeFilters.length || activeFilters.includes('Commands')) && (
                    <Command.Group>
                      <CommandGroupTitle title="Commands" />
                      <StyledCommandItem
                        keywords={['new', 'test']}
                        onSelect={() => {
                          closeCommandMenu();
                          createAndNavToTest();
                        }}
                      >
                        <div className="flex items-center gap-2">
                          <IconPlus size={16} />
                          <div>Create a new test</div>
                        </div>
                        <div className="opacity-0 group-data-[selected=true]:opacity-60">Command</div>
                      </StyledCommandItem>
                      <StyledCommandItem
                        keywords={['run', 'test', 'suite']}
                        onSelect={async () => {
                          closeCommandMenu();
                          await router.push(`/project/${projectId}/testSuite`);
                        }}
                      >
                        <div className="flex items-center gap-2">
                          <IconPlayerPlay size={16} />
                          <div>Run a test suite</div>
                        </div>
                        <div className="opacity-0 group-data-[selected=true]:opacity-60">Command</div>
                      </StyledCommandItem>
                      <StyledCommandItem
                        keywords={['orgs', 'manage', 'team', 'member', 'invite', 'kick', 'leave']}
                        onSelect={() => {
                          closeCommandMenu();
                          redirectToOrgPage();
                        }}
                      >
                        <div className="flex items-center gap-2">
                          <IconBuildingCommunity size={16} />
                          <div>Manage team members</div>
                        </div>
                        <div className="opacity-0 group-data-[selected=true]:opacity-60">Command</div>
                      </StyledCommandItem>
                      <StyledCommandItem
                        keywords={['light', 'dark', 'toggle', 'theme']}
                        onSelect={() => {
                          toggleColorScheme();
                          closeCommandMenu();
                        }}
                      >
                        <div className="flex items-center gap-2">
                          {colorScheme === 'dark' ?
                            <IconSun size={16} />
                          : <IconMoonStars size={16} />}
                          <div>{colorScheme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}</div>
                        </div>
                        <div className="opacity-0 group-data-[selected=true]:opacity-60">Command</div>
                      </StyledCommandItem>
                    </Command.Group>
                  )}

                  {/* Tests */}
                  {(!activeFilters.length || activeFilters.includes('Tests')) && tests.length > 0 && (
                    <Command.Group className="py-4">
                      <CommandGroupTitle title="Tests" />
                      {tests?.map((test) => (
                        <StyledCommandItem
                          onSelect={async () => {
                            closeCommandMenu();
                            await router.push(`/project/${projectId}/test/${test.id}`);
                          }}
                          value={test.id} // allows us to search by name or id
                          keywords={[test.name]}
                          key={test.id}
                        >
                          <div>{test.name}</div>
                          <div className="flex-shrink-0 opacity-0 group-data-[selected=true]:opacity-60">Test</div>
                        </StyledCommandItem>
                      ))}
                    </Command.Group>
                  )}

                  {/* Test drafts */}
                  {(!activeFilters.length || activeFilters.includes('Test Drafts')) && testDrafts.length > 0 && (
                    <Command.Group className="py-4">
                      <CommandGroupTitle title="Test Drafts" />
                      {testDrafts?.map((testDraft) => (
                        <StyledCommandItem
                          onSelect={async () => {
                            closeCommandMenu();
                            await router.push(`/project/${projectId}/testDraft/${testDraft.id}`);
                          }}
                          value={testDraft.id}
                          keywords={[testDraft.test?.name || '']}
                          key={testDraft.id}
                        >
                          <div>{testDraft.test?.name}</div>
                          <div className="flex-shrink-0 opacity-0 group-data-[selected=true]:opacity-60">
                            Test Draft
                          </div>
                        </StyledCommandItem>
                      ))}
                    </Command.Group>
                  )}
                  {/* Test suites */}
                  {(!activeFilters.length || activeFilters.includes('Test Suites')) && testSuites.length > 0 && (
                    <CommandTestSuiteGroup
                      testSuites={testSuites}
                      tests={tests}
                      projectId={projectId || ''}
                      closeModal={closeCommandMenu}
                    />
                  )}
                </div>
              </ScrollArea.Autosize>
              {hoveredTestSuite ?
                <SuiteDetailsPanel
                  testSuite={hoveredTestSuite}
                  tests={tests}
                  projectId={projectId || ''}
                  closeModal={closeCommandMenu}
                />
              : null}
              {hoveredTest || hoveredTestDraft ?
                <DetailsPanel
                  test={hoveredTest}
                  testDraft={hoveredTestDraft}
                  users={users}
                  testSuites={testSuites}
                  projectId={projectId || ''}
                  closeModal={closeCommandMenu}
                />
              : null}
            </div>
            <div className="flex items-center justify-between border-t border-[#dee2e6] px-4 py-2 text-xs text-gray-500 dark:border-[#424242] dark:text-gray-400">
              <div className="flex items-center gap-2">
                <div>Navigate</div>
                <div className="rounded-md bg-gray-100 p-1 dark:bg-[#424242]">
                  <IconArrowUp size={10} />
                </div>
                <div className="rounded-md bg-gray-100 p-1 dark:bg-[#424242]">
                  <IconArrowDown size={10} />
                </div>
              </div>
              <div className="flex items-center gap-2">
                <div>Select</div>
                <div className="rounded-md bg-gray-100 p-1 dark:bg-[#424242]">
                  <IconArrowBack size={10} />
                </div>
              </div>
            </div>
          </Command.List>
        </Command>
      </Modal>
    </>
  );
}
